import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { useUnreadMessages } from '../../../store/UnreadMessagesContext';
import { usePresence } from '../../../store/PresenceContext';
import { useStompClient } from '../../../store/StompContext';
import PopupMessage from '../../molecules/PopupMessage';
import { ChatMessageNotification } from '../../molecules/ChatMessageNotification';
import { ACTIVE_TAB_STORAGE_KEY, TAB_IDS } from '../../../constants/navigation';
import { buildAuthHeaders } from '../../../services/authTokenStore';
import { resolveGatewayBackedServiceBaseUrl } from '../../../services/serviceBaseUrl';
import { resolveUserDisplayName, resolveUserUsername } from '../../../utils/userIdentity';
import { normalizePresenceStatus } from '../../../utils/presenceStatus';

const API_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_USER_SERVICE_URL);
const CHAT_SERVICE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_CHAT_SERVICE_URL);

const CHAT_TAB_ID = TAB_IDS.CHAT;
const NOTIFICATION_AUTO_CLOSE_MS = 7000;
const PREVIEW_MAX_LENGTH = 180;
const MAX_NOTIFICATION_QUEUE = 5;
const MESSAGE_CACHE_LIMIT = 120;
const SENDER_CACHE_LIMIT = 200;
const UNREAD_REFRESH_DEBOUNCE_MS = 180;
const BOOTSTRAP_RETRY_DELAY_MS = 3000;
const AUTH_FAILURE_STATUSES = new Set([401, 403]);

const buildAuthenticatedRequestConfig = (config = {}) => ({
  ...config,
  withCredentials: true,
  headers: {
    ...buildAuthHeaders(),
    ...(config.headers || {}),
  },
});

const isAuthFailure = (error) => AUTH_FAILURE_STATUSES.has(error?.response?.status);

const trimCache = (cache, limit) => {
  if (cache.size <= limit) {
    return;
  }
  const iterator = cache.keys();
  while (cache.size > limit) {
    const nextKey = iterator.next();
    if (nextKey.done) {
      break;
    }
    cache.delete(nextKey.value);
  }
};

export default function UnreadWatcher() {
  const { setTotalUnread, setTotalGroupUnread, setPendingFriendRequests } = useUnreadMessages();
  const { upsertStatus, clearPresence } = usePresence();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const { client, isConnected } = useStompClient();

  const userIdRef = useRef(null);
  const mountedRef = useRef(false);
  const messageCacheRef = useRef(new Map());
  const senderCacheRef = useRef(new Map());
  const unreadRefreshRef = useRef({ inflight: null, timerId: null });
  const groupRefreshRef = useRef({ inflight: null, timerId: null });
  const friendRequestsRefreshRef = useRef({ inflight: null, timerId: null });
  const bootstrapTimerRef = useRef(null);
  const userTopicSubscriptionRef = useRef(null);
  const presenceSubscriptionRef = useRef(null);

  const [notificationQueue, setNotificationQueue] = useState([]);
  const [activeNotificationId, setActiveNotificationId] = useState(null);
  const [popupTrigger, setPopupTrigger] = useState(0);
  const [userId, setUserId] = useState(null);

  const truncateText = useCallback((text) => {
    if (!text) {
      return '';
    }
    const normalized = `${text}`.replace(/\s+/g, ' ').trim();
    if (normalized.length <= PREVIEW_MAX_LENGTH) {
      return normalized;
    }
    return `${normalized.slice(0, PREVIEW_MAX_LENGTH - 1).trimEnd()}…`;
  }, []);

  const refreshTotal = useCallback(() => {
    if (!mountedRef.current) {
      return Promise.resolve();
    }

    if (unreadRefreshRef.current.inflight) {
      return unreadRefreshRef.current.inflight;
    }

    const request = axios
      .get(`${CHAT_SERVICE_URL}/api/chat/unread-total`, {
        ...buildAuthenticatedRequestConfig(),
      })
      .then((res) => {
        if (mountedRef.current) {
          setTotalUnread(Number(res.data) || 0);
        }
      })
      .catch(() => {
        // Ignore temporary network failures; UI can retry on next event.
      })
      .finally(() => {
        if (unreadRefreshRef.current.inflight === request) {
          unreadRefreshRef.current.inflight = null;
        }
      });

    unreadRefreshRef.current.inflight = request;
    return request;
  }, [setTotalUnread]);

  const refreshGroupTotal = useCallback(() => {
    if (!mountedRef.current) {
      return Promise.resolve();
    }
    if (groupRefreshRef.current.inflight) {
      return groupRefreshRef.current.inflight;
    }

    const request = axios
      .get(`${CHAT_SERVICE_URL}/api/chat/groups/unread-total`, {
        ...buildAuthenticatedRequestConfig(),
      })
      .then((res) => {
        if (mountedRef.current) {
          setTotalGroupUnread(Number(res.data) || 0);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (groupRefreshRef.current.inflight === request) {
          groupRefreshRef.current.inflight = null;
        }
      });

    groupRefreshRef.current.inflight = request;
    return request;
  }, [setTotalGroupUnread]);

  const refreshFriendRequests = useCallback(() => {
    if (!mountedRef.current) {
      return Promise.resolve();
    }
    if (friendRequestsRefreshRef.current.inflight) {
      return friendRequestsRefreshRef.current.inflight;
    }

    const request = axios
      .get(`${API_URL}/api/users/me/friends/requests/incoming`, {
        ...buildAuthenticatedRequestConfig(),
      })
      .then((res) => {
        if (mountedRef.current) {
          const count = Array.isArray(res.data) ? res.data.length : 0;
          setPendingFriendRequests(count);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (friendRequestsRefreshRef.current.inflight === request) {
          friendRequestsRefreshRef.current.inflight = null;
        }
      });

    friendRequestsRefreshRef.current.inflight = request;
    return request;
  }, [setPendingFriendRequests]);

  const scheduleRefreshTotal = useCallback(() => {
    if (!mountedRef.current) {
      return;
    }
    if (unreadRefreshRef.current.timerId) {
      return;
    }

    unreadRefreshRef.current.timerId = window.setTimeout(() => {
      unreadRefreshRef.current.timerId = null;
      refreshTotal();
    }, UNREAD_REFRESH_DEBOUNCE_MS);
  }, [refreshTotal]);

  const scheduleGroupRefresh = useCallback(() => {
    if (!mountedRef.current) {
      return;
    }
    if (groupRefreshRef.current.timerId) {
      return;
    }

    groupRefreshRef.current.timerId = window.setTimeout(() => {
      groupRefreshRef.current.timerId = null;
      refreshGroupTotal();
    }, UNREAD_REFRESH_DEBOUNCE_MS);
  }, [refreshGroupTotal]);

  const scheduleFriendRequestsRefresh = useCallback(() => {
    if (!mountedRef.current) {
      return;
    }
    if (friendRequestsRefreshRef.current.timerId) {
      return;
    }

    friendRequestsRefreshRef.current.timerId = window.setTimeout(() => {
      friendRequestsRefreshRef.current.timerId = null;
      refreshFriendRequests();
    }, UNREAD_REFRESH_DEBOUNCE_MS);
  }, [refreshFriendRequests]);

  const fetchMessageDetails = useCallback(async (chatId, messageId) => {
    if (!messageId) {
      return null;
    }

    if (messageCacheRef.current.has(messageId)) {
      return messageCacheRef.current.get(messageId);
    }

    try {
      const response = await axios.get(`${CHAT_SERVICE_URL}/api/chat/message/${messageId}`, {
        ...buildAuthenticatedRequestConfig(),
      });

      const message = response.data;
      if (!message || message.chatId !== chatId) {
        messageCacheRef.current.set(messageId, null);
        trimCache(messageCacheRef.current, MESSAGE_CACHE_LIMIT);
        return null;
      }

      messageCacheRef.current.set(messageId, message);
      trimCache(messageCacheRef.current, MESSAGE_CACHE_LIMIT);
      return message;
    } catch (error) {
      messageCacheRef.current.set(messageId, null);
      trimCache(messageCacheRef.current, MESSAGE_CACHE_LIMIT);
      return null;
    }
  }, []);

  const fetchUserSummary = useCallback(async (userId) => {
    if (!userId) {
      return null;
    }

    if (senderCacheRef.current.has(userId)) {
      return senderCacheRef.current.get(userId);
    }

    try {
      const response = await axios.get(`${API_URL}/api/users/${userId}`, {
        ...buildAuthenticatedRequestConfig(),
      });

      const summary = {
        username: resolveUserUsername(response.data) || '',
        displayName: resolveUserDisplayName(response.data) || '',
      };

      senderCacheRef.current.set(userId, summary);
      trimCache(senderCacheRef.current, SENDER_CACHE_LIMIT);
      return summary;
    } catch (error) {
      senderCacheRef.current.set(userId, null);
      trimCache(senderCacheRef.current, SENDER_CACHE_LIMIT);
      return null;
    }
  }, []);

  const isChatTabActive = useCallback(() => {
    try {
      return localStorage.getItem(ACTIVE_TAB_STORAGE_KEY) === CHAT_TAB_ID;
    } catch (error) {
      return false;
    }
  }, []);

  const enqueueNotification = useCallback((notification) => {
    setNotificationQueue((prev) => {
      if (prev.some((item) => item.id === notification.id)) {
        return prev;
      }

      const updated = [...prev, notification];
      if (updated.length > MAX_NOTIFICATION_QUEUE) {
        updated.shift();
      }
      return updated;
    });
  }, []);

  const handleSummaryEvent = useCallback(
    async (payload) => {
      const chatId = payload?.chatId;
      const messageId = payload?.messageId;

      if (!chatId || !messageId || isChatTabActive()) {
        return;
      }

      const message = await fetchMessageDetails(chatId, messageId);
      if (!mountedRef.current || !message) {
        return;
      }

      if (String(message.senderId) === String(userIdRef.current)) {
        return;
      }

      const preview = truncateText(message.content);
      if (!preview) {
        return;
      }

      const senderSummary = await fetchUserSummary(message.senderId);
      if (!mountedRef.current) {
        return;
      }

      const senderName = senderSummary?.displayName || senderSummary?.username || `User ${message.senderId}`;

      enqueueNotification({
        id: messageId,
        chatId,
        senderId: message.senderId,
        senderName,
        preview,
      });
    },
    [enqueueNotification, fetchMessageDetails, fetchUserSummary, isChatTabActive, truncateText],
  );

  const handlePresenceEvent = useCallback((payload, options) => {
    const normalized = normalizePresenceStatus(payload, { requireEpoch: true });
    if (!normalized) {
      return;
    }
    upsertStatus(normalized, options);
  }, [upsertStatus]);

  useEffect(() => {
    const refreshState = unreadRefreshRef.current;
    const groupRefreshState = groupRefreshRef.current;
    const friendRequestsRefreshState = friendRequestsRefreshRef.current;

    const teardown = () => {
      if (bootstrapTimerRef.current) {
        window.clearTimeout(bootstrapTimerRef.current);
        bootstrapTimerRef.current = null;
      }

      if (refreshState.timerId) {
        clearTimeout(refreshState.timerId);
        refreshState.timerId = null;
      }

      if (friendRequestsRefreshState.timerId) {
        clearTimeout(friendRequestsRefreshState.timerId);
        friendRequestsRefreshState.timerId = null;
      }

      if (userTopicSubscriptionRef.current) {
        userTopicSubscriptionRef.current.unsubscribe();
        userTopicSubscriptionRef.current = null;
      }

      if (presenceSubscriptionRef.current) {
        presenceSubscriptionRef.current.unsubscribe();
        presenceSubscriptionRef.current = null;
      }

      userIdRef.current = null;
      setUserId(null);
      messageCacheRef.current.clear();
      senderCacheRef.current.clear();
    };

    if (!isLoggedIn) {
      mountedRef.current = false;
      teardown();
      clearPresence();
      setNotificationQueue([]);
      setActiveNotificationId(null);
      setPopupTrigger(0);
      return () => {
        teardown();
      };
    }

    mountedRef.current = true;
    let cancelled = false;

    const bootstrap = async () => {
      if (cancelled) {
        return;
      }

      try {
        const me = await axios.get(`${API_URL}/api/auth/me`, buildAuthenticatedRequestConfig());
        const uid = me.data?.id;
        if (!uid || cancelled) {
          return;
        }

        userIdRef.current = uid;
        setUserId(uid);

        await Promise.all([refreshTotal(), refreshGroupTotal(), refreshFriendRequests()]);

        if (cancelled) {
          return;
        }
        bootstrapTimerRef.current = null;
      } catch (error) {
        if (cancelled || !isLoggedIn) {
          return;
        }
        if (isAuthFailure(error)) {
          return;
        }
        if (bootstrapTimerRef.current) {
          window.clearTimeout(bootstrapTimerRef.current);
        }
        bootstrapTimerRef.current = window.setTimeout(bootstrap, BOOTSTRAP_RETRY_DELAY_MS);
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      teardown();
      if (groupRefreshState.timerId) {
        window.clearTimeout(groupRefreshState.timerId);
        groupRefreshState.timerId = null;
      }
      groupRefreshState.inflight = null;
      if (friendRequestsRefreshState.timerId) {
        window.clearTimeout(friendRequestsRefreshState.timerId);
        friendRequestsRefreshState.timerId = null;
      }
      friendRequestsRefreshState.inflight = null;
    };
  }, [
    isLoggedIn,
    clearPresence,
    refreshTotal,
    refreshGroupTotal,
    refreshFriendRequests,
  ]);

  useEffect(() => {
    if (!client || !isConnected || !isLoggedIn || !userId) {
      return;
    }

    if (userTopicSubscriptionRef.current) {
      userTopicSubscriptionRef.current.unsubscribe();
      userTopicSubscriptionRef.current = null;
    }
    if (presenceSubscriptionRef.current) {
      presenceSubscriptionRef.current.unsubscribe();
      presenceSubscriptionRef.current = null;
    }

    userTopicSubscriptionRef.current = client.subscribe(`/topic/user/${userId}`, async (message) => {
      scheduleRefreshTotal();

      if (!mountedRef.current || !message?.body) {
        return;
      }

      try {
        const payload = JSON.parse(message.body);
        if (payload?.type === 'NEW_MESSAGE') {
          await handleSummaryEvent(payload);
        } else if (payload?.type === 'MENTION') {
          if (!isChatTabActive()) {
            const senderId = payload?.senderId;
            const senderSummary = await fetchUserSummary(senderId);
            const senderName = senderSummary?.displayName || senderSummary?.username || `User ${senderId}`;

            enqueueNotification({
              id: `mention-${payload?.messageId || payload?.timestamp || Math.random()}`,
              chatId: payload?.chatId || payload?.channelId,
              senderId,
              senderName,
              preview: truncateText(payload?.content || ''),
            });
          }
        } else if (payload?.type === 'PRESENCE') {
          handlePresenceEvent(payload);
        } else if (payload?.type === 'GROUP_CHANNEL_UNREAD') {
          if (typeof payload?.groupUnreadTotal === 'number') {
            setTotalGroupUnread(Number(payload.groupUnreadTotal));
          } else {
            scheduleGroupRefresh();
          }
          if (typeof payload?.unreadCount === 'number' && payload?.channelId) {
            // No-op; BandsPage handles per-channel state via WebSocket subscription.
          }
        } else if (payload?.type === 'FRIEND_REQUESTS_UPDATED') {
          if (typeof payload?.incomingCount === 'number') {
            setPendingFriendRequests(Number(payload.incomingCount));
          } else {
            scheduleFriendRequestsRefresh();
          }
        }
      } catch (error) {
        // ignore malformed payloads
      }
    }, buildAuthHeaders());

    presenceSubscriptionRef.current = client.subscribe('/topic/presence', (message) => {
      if (!mountedRef.current || !message?.body) {
        return;
      }
      try {
        const payload = JSON.parse(message.body);
        handlePresenceEvent({
          userId: payload?.userId,
          online: payload?.online,
          lastSeen: payload?.lastSeen,
          epoch: payload?.epoch,
        }, {
          allowUntracked: false,
          track: false,
        });
      } catch (error) {
        // ignore malformed payloads
      }
    }, buildAuthHeaders());

    return () => {
      if (userTopicSubscriptionRef.current) {
        userTopicSubscriptionRef.current.unsubscribe();
        userTopicSubscriptionRef.current = null;
      }
      if (presenceSubscriptionRef.current) {
        presenceSubscriptionRef.current.unsubscribe();
        presenceSubscriptionRef.current = null;
      }
    };
  }, [
    client,
    enqueueNotification,
    fetchUserSummary,
    handlePresenceEvent,
    handleSummaryEvent,
    isConnected,
    isChatTabActive,
    isLoggedIn,
    scheduleFriendRequestsRefresh,
    scheduleGroupRefresh,
    scheduleRefreshTotal,
    setPendingFriendRequests,
    setTotalGroupUnread,
    truncateText,
    userId,
  ]);

  useEffect(() => {
    const next = notificationQueue[0] || null;
    const nextId = next?.id || null;

    if (nextId !== activeNotificationId) {
      setActiveNotificationId(nextId);
      if (nextId) {
        setPopupTrigger((prev) => prev + 1);
      }
    }
  }, [activeNotificationId, notificationQueue]);

  const handleNotificationClosed = useCallback(() => {
    setNotificationQueue((prev) => prev.slice(1));
  }, []);

  const activeNotification = notificationQueue[0] || null;

  if (!activeNotification) {
    return null;
  }

  return (
    <PopupMessage
      variant="chat"
      popupTrigger={popupTrigger}
      duration={NOTIFICATION_AUTO_CLOSE_MS}
      onClose={handleNotificationClosed}
    >
      <ChatMessageNotification
        senderId={activeNotification.senderId}
        senderName={activeNotification.senderName}
        messagePreview={activeNotification.preview}
      />
    </PopupMessage>
  );
}
